/**
 * Integrations → Data — download everything this Company owns (#374).
 *
 * The section exists so leaving HireOS never needs the developer. It is one
 * row, not three: the earlier shape put a button on every variant of the same
 * export, which made the differences between them a reading exercise. What is
 * in the zip hangs off the row title as a tooltip, and the two choices sit in
 * the dialog the button opens, each with the consequence of ticking it.
 *
 * It is a drilled-into space inside Integrations beside Xero and the phone
 * number, so the header above it is Integrations' (like Communications and
 * Accounting & payments); this component renders the row alone.
 *
 * Bank account numbers are a per-run tick rather than a saved setting: a
 * persisted switch would make payment data ride along in every later export,
 * silently.
 */
import { useState } from "react";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { useDismissable } from "../../lib/use-dismissable";
import { useModalScrollLock } from "../../lib/use-modal-scroll-lock";
import { SettingRow } from "../../components/ui/SettingRow";
import { downloadCompanyExport } from "../../lib/export-api";
import chrome from "./settings.module.css";
import styles from "./DataSection.module.css";

export function DataSection() {
  const [open, setOpen] = useState(false);
  const [documents, setDocuments] = useState(true);
  const [bankDetails, setBankDetails] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      await downloadCompanyExport({ documents, bankDetails });
      setOpen(false);
      // Payment data is opted into one run at a time.
      setBankDetails(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      {/* Section-scoped failure, at the top of the section that caused it. */}
      {error && !open && (
        <Banner
          variant="error"
          className={chrome.banner}
          onDismiss={() => setError(null)}
        >
          {error}
        </Banner>
      )}

      <SettingRow
        title="Export everything"
        tooltip="CSVs of your equipment, customers, crew, jobs and invoices, the PDFs you have sent, and a README naming each system's import screen."
        description="One zip file. Open it in Excel, or hand it to another rental or accounting system."
      >
        <Button size="sm" onClick={() => setOpen(true)}>
          Export
        </Button>
      </SettingRow>

      <ExportDialog
        open={open}
        documents={documents}
        bankDetails={bankDetails}
        running={running}
        error={error}
        onDocuments={setDocuments}
        onBankDetails={setBankDetails}
        onExport={() => void run()}
        onCancel={() => {
          setOpen(false);
          setError(null);
        }}
      />
    </div>
  );
}

/**
 * The two choices, each stated with what ticking it costs. A dialog rather
 * than rows on the page: neither choice is a setting, so nothing here should
 * look like it saves.
 */
function ExportDialog({
  open,
  documents,
  bankDetails,
  running,
  error,
  onDocuments,
  onBankDetails,
  onExport,
  onCancel,
}: {
  open: boolean;
  documents: boolean;
  bankDetails: boolean;
  running: boolean;
  error: string | null;
  onDocuments: (next: boolean) => void;
  onBankDetails: (next: boolean) => void;
  onExport: () => void;
  onCancel: () => void;
}) {
  // DESIGN-SYSTEM.md Modals: the backdrop owns click-to-close, Escape closes the
  // topmost surface, and the page behind does not scroll.
  useModalScrollLock(open);
  useDismissable({ open, onDismiss: onCancel, pointer: "none" });

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-label="Export your data"
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className={styles.title}>Export your data</h3>

        <div className={styles.options}>
          <ExportOption
            id="export-documents"
            label="Include the documents you have sent"
            detail="The PDFs, as well as the records. Leave this off for a small file you can email."
            checked={documents}
            disabled={running}
            onChange={onDocuments}
          />
          <ExportOption
            id="export-bank-details"
            label="Include your crew's bank account numbers"
            detail="Off by default. Tick it only if the system you are moving to needs them, and treat the file as payment data."
            checked={bankDetails}
            disabled={running}
            onChange={onBankDetails}
          />
        </div>

        {error && (
          <Banner variant="error" className={chrome.bannerBelow}>
            {error}
          </Banner>
        )}

        <div className={styles.actions}>
          <Button variant="secondary" onClick={onCancel} disabled={running}>
            Cancel
          </Button>
          <Button loading={running} onClick={onExport}>
            Export
          </Button>
        </div>
      </div>
    </div>
  );
}

function ExportOption({
  id,
  label,
  detail,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  detail: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className={styles.option}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className={styles.checkbox}
      />
      <div className={styles.optionText}>
        <label htmlFor={id} className={styles.optionLabel}>
          {label}
        </label>
        <p className={styles.optionDetail}>{detail}</p>
      </div>
    </div>
  );
}
